import { KeyboardEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';

import DesactivarColaborador from '@components/Colaboradores/DesactivarColaborador';
import ReactivarColaborador from '@components/Colaboradores/ReactivarColaborador';
import { QontyEmptyState } from '@components/QontyEmptyState';
import { QontyLoading } from '@components/QontyLoading';
import ResultPopup from '@components/General/ResultPopup';
import { UserLimitReachedPopup } from '@components/General/ProhibitedPopups';
import { TabScreenWrapper } from '@components/General/TabScreen';
import { usePopup } from '@contexts/popupContext';
import { Application } from '@core/Application';
import { CollaboratorsAnalyticsHelper } from '@core/Analytics/CollaboratorsAnalyticsHelper';
import { Flap } from '@models/Flap';
import { Organization } from '@models/Organization';
import { HollowedButton, IconButton, NormalButton, RefreshButton } from '@shared/designSystem/Button';
import { Loader } from '@shared/designSystem/Loader';
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from '@shared/designSystem/Containers';
import { Modal } from '@shared/designSystem/messages/Modal';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import SearchBar, { SearchBarRef } from '@shared/designSystem/SearchBar';
import { TextInput, TextInputRef } from '@shared/designSystem/TextInput';
import { Text } from '@shared/designSystem/Texts';
import UserCard from '@shared/designSystem/UserCard';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { UsersService } from '@services/UsersService';

import {
    CollaboratorCatalogRow,
    CollaboratorsTab,
    useColaboradoresScreenViewModel,
} from './ColaboradoresScreenViewModel';
import ColaboradorDetailModalScreen from './ColaboradorDetailModalScreen';

interface ColaboradoresScreenProps {
    org: Organization;
}

const formatPhone = (value: string) => {
    if (!value) {
        return ''
    }
    const sanitizedValue = (value ?? '').replace(/^whatsapp:/i, '').trim();

    if (!sanitizedValue) {
        return '';
    }

    return sanitizedValue.startsWith('+') ? sanitizedValue : `+${sanitizedValue}`;
};
const columnWidths = {
    collaborator: '36%',
    phone: '22%',
    identification: '20%',
    company: '14%',
    actions: '8%',
};

const CreateCollaboratorModal = ({
    isOpen,
    creating,
    draft,
    errors,
    onClose,
    onDraftChange,
    onSubmit,
}: {
    isOpen: boolean;
    creating: boolean;
    draft: { name: string; phone: string; email: string };
    errors: { name: string; phone: string; email: string };
    onClose: () => void;
    onDraftChange: (field: 'name' | 'phone' | 'email', value: string) => void;
    onSubmit: () => void;
}) => {
    const nameInputRef = useRef<TextInputRef>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timeout = window.setTimeout(() => {
            nameInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={creating ? () => {} : onClose}
            title={'Agregar colaborador'}
            width="720px"
            maxWidth="92vw"
        >
            <VerticalContainer width="100%" gap={designSystemStyles.gapMd} alignment="left">
                <Text
                    text={'Completa los datos iniciales para crear un nuevo colaborador.'}
                    color={designSystemStyles.colorTextSecondary}
                />
                <TextInput
                    ref={nameInputRef}
                    label={'Nombre'}
                    value={draft.name}
                    placeholder={'Nombre'}
                    error={errors.name}
                    loading={creating}
                    onChange={(value) => onDraftChange('name', value.replace(/[^a-zA-Z\u00C0-\u00FF ]/g, ''))}
                    onEnterPress={onSubmit}
                />
                <TextInput
                    label={'Celular'}
                    value={draft.phone}
                    placeholder={'+5491112345678'}
                    type="tel"
                    inputMode="tel"
                    error={errors.phone}
                    loading={creating}
                    onChange={(value) => {
                        const digits = value.replace(/\D/g, '');
                        onDraftChange('phone', value.startsWith('+') ? `+${digits}` : digits);
                    }}
                    onEnterPress={onSubmit}
                />
                <TextInput
                    label={'Email'}
                    value={draft.email}
                    placeholder={'Email'}
                    type="email"
                    inputMode="email"
                    error={errors.email}
                    loading={creating}
                    onChange={(value) => onDraftChange('email', value)}
                    onEnterPress={onSubmit}
                />
                <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                    <HollowedButton
                        text={'Cancelar'}
                        onClick={onClose}
                        disabled={creating}
                        width="140px"
                    />
                    <NormalButton
                        text={'Guardar'}
                        onClick={onSubmit}
                        loading={creating}
                        width="140px"
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

// Loads the next page when the user is this close to the bottom
const INFINITE_SCROLL_THRESHOLD_PX = 200;

const CollaboratorsTable = memo(({
    collaborators,
    tab,
    onOpenCollaborator,
    onStatusAction,
    hasMore = false,
    loadingMore = false,
    onEndReached,
}: {
    collaborators: CollaboratorCatalogRow[];
    tab: CollaboratorsTab;
    onOpenCollaborator: (collaborator: CollaboratorCatalogRow) => void;
    onStatusAction: (collaborator: CollaboratorCatalogRow) => void;
    hasMore?: boolean;
    loadingMore?: boolean;
    onEndReached?: () => void;
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const loadMoreIfNearBottom = useCallback(() => {
        const container = scrollRef.current;
        if (!container || !hasMore || loadingMore || !onEndReached) {
            return;
        }

        const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceToBottom < INFINITE_SCROLL_THRESHOLD_PX) {
            onEndReached();
        }
    }, [hasMore, loadingMore, onEndReached]);

    // If the loaded rows don't fill the table there's nothing to scroll, so keep loading
    useEffect(() => {
        loadMoreIfNearBottom();
    }, [collaborators.length, loadMoreIfNearBottom]);

    const handleCollaboratorKeyDown = (
        event: KeyboardEvent<HTMLDivElement>,
        collaborator: CollaboratorCatalogRow,
    ) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        onOpenCollaborator(collaborator);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: designSystemStyles.gapMd, width: '100%' }}>
            <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                <TableHeaderCell text={'Colaborador'} width={columnWidths.collaborator} />
                <TableHeaderCell text={'Celular'} width={columnWidths.phone} />
                <TableHeaderCell text={'Identificación'} width={columnWidths.identification} />
                <TableHeaderCell text={'Empresa'} width={columnWidths.company} />
                <TableHeaderCell text={''} width={columnWidths.actions} justifyContent="right" />
            </TableHeaderContainer>
            <div
                ref={scrollRef}
                onScroll={loadMoreIfNearBottom}
                style={{ display: 'flex', flexDirection: 'column', gap: designSystemStyles.gapNone, overflowY: 'auto', flex: 1, minHeight: 0, width: '100%' }}
            >
                {collaborators.map((collaborator) => (
                    <TableRowContainer key={collaborator.id}>
                        <HorizontalContainer width={columnWidths.collaborator}>
                            <div
                                role="button"
                                tabIndex={0}
                                aria-label={`Abrir detalle de ${collaborator.name}`}
                                onClick={() => onOpenCollaborator(collaborator)}
                                onKeyDown={(event) => handleCollaboratorKeyDown(event, collaborator)}
                                style={{ cursor: 'pointer', width: '100%' }}
                            >
                                <UserCard name={collaborator.name} imageURL={collaborator.photoUrl || undefined} width="100%" />
                            </div>
                        </HorizontalContainer>
                        <TableCell text={formatPhone(collaborator.mobilePhone)} width={columnWidths.phone} />
                        <TableCell text={collaborator.dni || ''} width={columnWidths.identification} />
                        <TableCell text={collaborator.empresa || ''} width={columnWidths.company} />
                        <HorizontalContainer width={columnWidths.actions} justifyContent="right" gap={designSystemStyles.gapSm}>
                            <IconButton
                                onClick={() => onOpenCollaborator(collaborator)}
                                iconType={tab === 'active' ? 'edit' : 'eye'}
                                tooltip={tab === 'active' ? 'Editar colaborador' : 'Ver colaborador'}
                            />
                            <IconButton
                                onClick={() => onStatusAction(collaborator)}
                                iconType={tab === 'active' ? 'delete' : 'check'}
                                tooltip={tab === 'active' ? 'Desactivar colaborador' : 'Reactivar colaborador'}
                                iconColor={designSystemStyles.colorText}
                            />
                        </HorizontalContainer>
                    </TableRowContainer>
                ))}
                {loadingMore ? (
                    <HorizontalContainer width="100%" justifyContent="center" paddingTop={designSystemStyles.gapMd}>
                        <Loader height="32px" width="32px" />
                    </HorizontalContainer>
                ) : null}
            </div>
        </div>
    );
});

export default function ColaboradoresScreen({ org }: ColaboradoresScreenProps) {
    const { showPopup } = usePopup();
    const usersService = useMemo(
        () => new UsersService(Application.shared().coreDependencies.httpClient),
        [],
    );
    const searchBarRef = useRef<SearchBarRef>(null);
    const [selectedCollaborator, setSelectedCollaborator] = useState<CollaboratorCatalogRow | null>(null);

    const viewModel = useColaboradoresScreenViewModel({
        orgID: org.id,
        usersService,
        onCreateSuccess: (collaboratorName) => {
            ToastManager.shared().showSuccessMessage(`Colaborador ${collaboratorName} guardado`);
        },
        onCreateError: (error) => {
            const message = error instanceof Error ? error.message : '';
            if (message === 'USER_LIMIT_REACHED') {
                showPopup(() => <UserLimitReachedPopup />);
                return;
            }

            showPopup(() => (
                <ResultPopup succesful={false} result={'Hubo un error al agregar el usuario...'} />
            ));
        },
        onExportError: () => {
            ToastManager.shared().showErrorMessage('No se pudo exportar el listado de colaboradores.'.localized());
        },
    });

    useEffect(() => {
        CollaboratorsAnalyticsHelper.trackShown(viewModel.selectedTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { selectedTab, applyStatusChange, getCollaboratorDetail, updateCollaboratorLocally } = viewModel;

    const handleOpenCollaborator = useCallback(async (collaborator: CollaboratorCatalogRow) => {
        CollaboratorsAnalyticsHelper.trackDetailOpened(selectedTab, collaborator.id);
        const detail = await getCollaboratorDetail(collaborator);
        setSelectedCollaborator(detail);
    }, [getCollaboratorDetail, selectedTab]);

    const handleCollaboratorChange = (collaborator: CollaboratorCatalogRow) => {
        setSelectedCollaborator(collaborator);
        updateCollaboratorLocally(collaborator);
    };

    const handleStatusAction = useCallback((collaborator: CollaboratorCatalogRow) => {
        const popupContent = selectedTab === 'active'
            ? (
                <DesactivarColaborador
                    row={collaborator}
                    colaborador={collaborator.name}
                    orgID={org.id}
                    onDone={() => {
                        CollaboratorsAnalyticsHelper.trackDeactivateConfirmed(collaborator.id);
                        applyStatusChange(collaborator, true);
                    }}
                />
            )
            : (
                <ReactivarColaborador
                    row={collaborator}
                    colaborador={collaborator.name}
                    orgID={org.id}
                    onDone={() => {
                        CollaboratorsAnalyticsHelper.trackReactivateConfirmed(collaborator.id);
                        applyStatusChange(collaborator, false);
                    }}
                />
            );

        showPopup(() => popupContent, false);
    }, [org.id, showPopup, selectedTab, applyStatusChange]);

    if (!org.flags?.hasWhitelistColaborators) {
        return <Navigate to="/" />;
    }

    const handleRefresh = () => {
        CollaboratorsAnalyticsHelper.trackRefreshed(viewModel.selectedTab);
        void viewModel.refresh().catch((error) => {
            console.error(error);
        });
    };

    const handleTabSelected = (screen: string) => {
        const tab = screen === 'deactivated' ? 'deactivated' : 'active';
        viewModel.setSelectedTab(tab);
        CollaboratorsAnalyticsHelper.trackTabSelected(tab);
    };

    const renderContent = (tab: CollaboratorsTab) => (
        <VerticalContainer
            width="100%"
            height="100%"
            gap={designSystemStyles.gapLg}
            paddingTop={designSystemStyles.paddingZero}
        >
            <HorizontalContainer height={designSystemStyles.sizeRowMedium}>
                <HorizontalContainer width="100%" gap={designSystemStyles.gapSm} alignment="center">
                    <div style={{ width: '460px', maxWidth: '42vw' }}>
                        <SearchBar
                            ref={searchBarRef}
                            placeholder={'filtrar'}
                            value={viewModel.searchQuery}
                            onChange={viewModel.setSearchQuery}
                        />
                    </div>
                    <RefreshButton text="" onClick={handleRefresh} />
                </HorizontalContainer>
                <HorizontalContainer width="auto" gap={designSystemStyles.gapSm} alignment="center">
                    <HollowedButton
                        text={'Exportar datos'.localized()}
                        onClick={() => void viewModel.exportCurrentTab()}
                        disabled={viewModel.collaboratorsByTabCount === 0 || viewModel.exporting}
                        width="160px"
                    />
                    {tab === 'active' ? (
                        <NormalButton
                            text={'Agregar'}
                            onClick={viewModel.openCreateModal}
                            width="160px"
                        />
                    ) : null}
                </HorizontalContainer>
            </HorizontalContainer>

            {viewModel.loading && viewModel.visibleCollaborators.length === 0 ? (
                <QontyLoading
                    text={'Cargando colaboradores...'}
                    withQonty={true}
                    darkMode={false}
                />
            ) : viewModel.visibleCollaborators.length ? (
                <CollaboratorsTable
                    collaborators={viewModel.visibleCollaborators}
                    tab={tab}
                    onOpenCollaborator={handleOpenCollaborator}
                    onStatusAction={handleStatusAction}
                    hasMore={tab === 'active' && viewModel.hasMore}
                    loadingMore={viewModel.loadingMore}
                    onEndReached={viewModel.loadMore}
                />
            ) : (
                <QontyEmptyState
                    text={
                        viewModel.searchQuery
                            ? 'No se encontraron colaboradores para la búsqueda.'.localized()
                            : tab === 'active'
                                ? 'No hay colaboradores activos.'.localized()
                                : 'No hay colaboradores desactivados.'.localized()
                    }
                    darkMode={false}
                />
            )}
        </VerticalContainer>
    );

    const flaps: Flap[] = [
        {
            id: 'active',
            label: 'Activos',
            content: renderContent('active'),
        },
        {
            id: 'deactivated',
            label: 'Desactivados',
            content: renderContent('deactivated'),
        },
    ];

    return (
        <>
            <TabScreenWrapper
                title="Colaboradores"
                subtitle="Gestiona los colaboradores de la empresa que recibirán documentos"
                flaps={flaps}
                flag={org.flags?.hasWhitelistColaborators}
                clickedScreen={viewModel.selectedTab}
                setClickedScreen={handleTabSelected}
            />

            <CreateCollaboratorModal
                isOpen={viewModel.isCreateModalOpen}
                creating={viewModel.creating}
                draft={viewModel.createDraft}
                errors={viewModel.createErrors}
                onClose={viewModel.closeCreateModal}
                onDraftChange={viewModel.updateCreateDraft}
                onSubmit={viewModel.submitCreateCollaborator}
            />
            <ColaboradorDetailModalScreen
                isOpen={selectedCollaborator != null}
                org={org}
                collaborator={selectedCollaborator}
                onClose={() => setSelectedCollaborator(null)}
                onCollaboratorChange={handleCollaboratorChange}
                readOnly={selectedCollaborator?.deactivated ?? false}
            />
        </>
    );
}
